/** @flow */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import styled, { withTheme } from 'styled-components';
import Helmet from 'react-helmet';
import classnames from 'classnames';
import ReactCSSTransitionGroup from 'react-addons-css-transition-group';
import { BrowserRouter as Router, Route } from 'react-router-dom';
import Drawer from 'material-ui/Drawer';
import DrawerV2 from '@material-ui/core/Drawer';
import Backdrop from '@material-ui/core/Backdrop';
import { translate } from 'react-i18next';
import { ToastContainer, toast } from 'react-toastify';

import Radio from 'common/domain/radio';
import Snackbar from 'common/modules/ui/components/Snackbar';
import ScrollToTop from 'common/layout/ScrollToTop';
import { closeSnackbar } from 'common/modules/ui/actions/uiActions';
import type { SnackbarState } from 'common/modules/ui/types/State';
import { Layout } from 'statsradio/layout/styles';
import { head } from 'common/config/config';
import SplashScreen from 'common/modules/ui/components/SplashScreen';
import SessionManager from 'common/modules/session/SessionManager';
import StatsRadioSupportWidget from 'common/components/StatsRadioSupportWidget';

import type { AppState } from '../types/State';
import {
  closeSession,
  beginSession,
  renewSession,
  switchRadio,
} from '../modules/authentication/session/actions/sessionActions';
import Menu from './Menu';
import MenuV2 from './v2/Menu';
import Routes from './Routes';
import SalesEstimationNotificationContent from '../modules/notifications/components/salesNotifications/SalesNotificationContent';
import NavBarV2 from './v2/NavBar';
import DevFlag, { isDevFlagEnabled } from '../../common/components/DevFlag';
import NavBar from './NavBar';

const PaperComponent = styled.div`
  padding-top: ${props => props.theme.V3.metrics.navbar.height}px;
  z-index: ${props => props.theme.V3.metrics.navbar.zIndex - 1};
  width: ${props => props.theme.V3.metrics.menu.width}px;
`;

const SDrawer = styled(DrawerV2)`
  z-index: ${props => props.theme.V3.metrics.navbar.zIndex - 1};
`;

const ToolbarSpace = styled.div`
  min-height: ${props => props.theme.V3.metrics.navbar.height}px;
`;

const SBackdrop = styled(Backdrop)`
  top: ${props => props.theme.V3.metrics.navbar.height}px;
`;

const NOTIFICATION_RENDERERS = {
  'SALES_ESTIMATION': SalesEstimationNotificationContent,
};

const PRINTING_ROUTES = [
  '/proposition-printing',
  '/sales-flyer-printing',
  '/campaign-printing',
  '/reach-printing',
  '/listening-time-printing',
  '/dashboard-printing',
  '/demography-printing',
  '/history-printing',
];

type Props = {
  snackbar: SnackbarState;
  isFetched: boolean;
  isAuthenticated: boolean;
  t: any,
  theme: any,
  actions: {
    closeSession: () => mixed;
    closeSnackbar: () => mixed;
    beginSession: () => mixed,
    renewSession: () => mixed,
    switchRadio: (radio: Radio) => mixed,
  };
}

type State = {
  open: boolean;
  forceMenuDisplay: boolean;
}

class App extends Component<Props, State> {
  state = {
    open: false,
    forceMenuDisplay: true,
  };

  componentWillMount() {
    this.handleWindowResize();
    window.addEventListener('resize', this.handleWindowResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleWindowResize);
  }

  logout = () => {
    this.setState({ open: false });
    this.props.actions.closeSession();
  };

  handleWindowResize = () => {
    if (window.innerWidth > 1380) {
      this.setState({ forceMenuDisplay: true, open: false });
    } else {
      this.setState({ forceMenuDisplay: false, open: false });
    }
  };

  handleToggleDrawer = () => this.setState({ open: !this.state.open });

  isUnauthenticatedRoute = ({ pathname }) => {
    const unauthenticatedRoutes = [
      '/login',
      '/accept-invitation',
      '/forgot-password',
      '/reset-password',
      '/confirm-email',
      ...PRINTING_ROUTES,
    ];

    return unauthenticatedRoutes.includes(pathname);
  };

  renderStatsRadioSupportWidget = ({ pathname }) => {
    const ignoredRoutes = [...PRINTING_ROUTES];
    return ignoredRoutes.includes(pathname) ? null : <StatsRadioSupportWidget />;
  };

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    const menuChanged = nextState.forceMenuDisplay !== this.state.forceMenuDisplay
      || nextState.open !== this.state.open;
    return this.props !== nextProps || menuChanged;
  }

  render() {
    const {
      isFetched,
      isAuthenticated,
      actions,
    } = this.props;

    return (
      <React.Fragment>
        <Router>
          <ScrollToTop>
            <Route render={({ location }) => (
              <Layout>
                {this.renderStatsRadioSupportWidget(location)}
                <Helmet {...head} />
                {!this.isUnauthenticatedRoute(location) && isFetched && (
                  <div>
                    <DevFlag
                      flag={'UI_V2'}
                      fallback={
                        <React.Fragment>
                          <Drawer onRequestChange={this.handleToggleDrawer} docked={this.state.forceMenuDisplay} open={this.state.open || this.state.forceMenuDisplay}>
                            <Menu onLinkClick={this.handleToggleDrawer} />
                          </Drawer>
                          <NavBar
                            switchRadio={actions.switchRadio}
                            notificationRenderers={NOTIFICATION_RENDERERS}
                            forceMenuDisplay={this.state.forceMenuDisplay}
                            onMenuIconClick={this.handleToggleDrawer}
                          />
                        </React.Fragment>
                      }
                    >
                      <React.Fragment>
                        <ToolbarSpace />
                        <SDrawer
                          onClose={this.handleToggleDrawer}
                          open={this.state.open || this.state.forceMenuDisplay}
                          variant={this.state.forceMenuDisplay ? 'persistent' : 'temporary'}
                          PaperProps={{ component: PaperComponent }}
                          ModalProps={{ BackdropComponent: SBackdrop }}
                        >
                          <MenuV2 onLinkClick={this.handleToggleDrawer} />
                        </SDrawer>
                        <NavBarV2
                          forceMenuDisplay={this.state.forceMenuDisplay}
                          switchRadio={actions.switchRadio}
                          notificationRenderers={NOTIFICATION_RENDERERS}
                          onMenuIconClick={this.handleToggleDrawer}
                        />
                      </React.Fragment>
                    </DevFlag>
                  </div>
                )}
                <div className={classnames('app-content', {
                  expanded: this.state.forceMenuDisplay && !this.isUnauthenticatedRoute(location) && !isDevFlagEnabled('UI_V2'),
                  expandedV2: this.state.forceMenuDisplay && !this.isUnauthenticatedRoute(location) && isDevFlagEnabled('UI_V2'),
                  'with-app-bar': !this.isUnauthenticatedRoute(location) && !isDevFlagEnabled('UI_V2'),
                })
                }
                >
                  <SessionManager
                    isAuthenticated={isAuthenticated}
                    beginSession={this.props.actions.beginSession}
                    renewSession={this.props.actions.renewSession}
                  />
                  <ReactCSSTransitionGroup
                    transitionName="fade"
                    transitionAppear
                    transitionAppearTimeout={500}
                    transitionEnterTimeout={10000}
                    transitionLeaveTimeout={10000}
                  >
                    {isFetched ?
                      <Routes location={location} />
                      :
                      <SplashScreen />
                    }
                  </ReactCSSTransitionGroup>
                </div>
                <ToastContainer autoClose={5000} position={toast.POSITION.TOP_RIGHT} hideProgressBar />
                <Snackbar
                  type={this.props.snackbar.kind}
                  visible={this.props.snackbar.isShowing}
                  message={this.props.snackbar.message}
                  onClose={this.props.actions.closeSnackbar}
                />
              </Layout>
            )}
            />
          </ScrollToTop>
        </Router>
      </React.Fragment>
    );
  }
}

function mapStateToProps(state: AppState) {
  return {
    snackbar: state.ui.snackbar,
    isFetched: state.session.isFetched,
    isAuthenticated: state.session.isAuthenticated,
  };
}

function mapDispatchToProps(dispatch: *) {
  return {
    actions: bindActionCreators({
      closeSnackbar,
      closeSession,
      beginSession,
      renewSession,
      switchRadio,
    }, dispatch),
  };
}

export default translate()(connect(mapStateToProps, mapDispatchToProps)(withTheme(App)));



// WEBPACK FOOTER //
// ./src/statsradio/layout/App.js